import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Footer from "../components/Footer";
import { search, indexSize } from "../utils/searchIndex";
import { setPageMeta } from "../utils/meta";

const KIND_LABEL = { camera: "Camera", type: "Camera type", reference: "Reference", recipe: "Recipe" };

/**
 * The full results page behind the header search.
 *
 * noindex, deliberately. Search result pages are the classic case of
 * infinite low-value URLs — every query string is a new page saying
 * nothing the destinations don't say better. The query lives in the URL so
 * a result set can be shared or bookmarked; it just shouldn't be indexed.
 */
export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";

  const results = useMemo(() => search(query, 60), [query]);

  const grouped = useMemo(() => {
    const g = {};
    results.forEach((r) => { (g[r.kind] = g[r.kind] || []).push(r); });
    return g;
  }, [results]);

  useEffect(() => {
    setPageMeta({
      title: query ? `Search: ${query}` : "Search",
      description: "Search cameras, settings references and shot recipes.",
      path: "/search/",
      noindex: true,
    });
  }, [query]);

  return (
    <main className="search-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Search</span>
      </nav>

      <h1>Search</h1>

      <label className="visually-hidden" htmlFor="search-page-input">Search</label>
      <input
        id="search-page-input"
        className="search-page-input"
        type="search"
        value={query}
        placeholder="Camera model, or a setting like shutter, ISO, ND"
        onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
        autoFocus
      />

      {!query && (
        <p className="search-hint">
          {indexSize()} cameras, references and recipes are searchable. Everything runs in your
          browser, so this works with no connection.
        </p>
      )}

      {query && results.length === 0 && (
        <div className="search-none">
          <p>Nothing matches "{query}".</p>
          <p>
            Try the camera's model name on its own, or a setting, shutter, ISO, ND filter, white
            balance. You can also <Link to="/cameras">browse all cameras</Link> or{" "}
            <Link to="/builder">build a recipe for any combination</Link>.
          </p>
        </div>
      )}

      {query && results.length > 0 && (
        <>
          <p className="search-count data">
            {results.length} result{results.length === 1 ? "" : "s"}
          </p>
          {["camera", "type", "reference", "recipe"].map((kind) =>
            grouped[kind] ? (
              <section className="search-group" key={kind}>
                <h2>{KIND_LABEL[kind]}s <span className="data">{grouped[kind].length}</span></h2>
                <ul>
                  {grouped[kind].map((r) => (
                    <li key={r.path + r.title}>
                      <Link to={r.path}>
                        <span className="sr-title">{r.title}</span>
                        <span className="sr-sub">{r.subtitle}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null
          )}
        </>
      )}

      <div data-prerender-ready="true" style={{ display: "none" }} />
      <Footer />
    </main>
  );
}
